import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'
import { GraphContainer, GraphTitle, GraphExpandButton, GraphWrapper } from '../../CardStyles'
import { GraphProps } from '../../../utils/types'
import { formatYAxisLoss, prepareGraphData } from '../../../utils/graphs'
import { LOSS_COLOR, axisStyle } from '../../../utils/constants'
import LossTooltip from './tooltips/LossTooltip'

const LossGraph: React.FC<GraphProps> = ({ data, legendData, isExpanded, onExpand, hideTitle = false }) => {
  const { t, i18n } = useTranslation()
  
  const { filteredDisplayData, xAxisTicks } = useMemo(() => 
    prepareGraphData(data, legendData, isExpanded, ['loss']), 
    [data, legendData, isExpanded, i18n.language]
  );

  if (filteredDisplayData.length === 0) {
    return (
      <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
        {!hideTitle && (
          <GraphTitle>
            <Typography variant="subtitle2">{t('wan.graphs.loss.title')}</Typography>
            {!isExpanded && (
              <GraphExpandButton onClick={onExpand}>
                <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
              </GraphExpandButton>
            )}
          </GraphTitle>
        )}
        <Box sx={{ 
          height: isExpanded ? '100%' : 100, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            {t('wan.graphs.no_data')}
          </Typography>
        </Box>
      </GraphContainer>
    )
  }  

  return (
    <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
      {!hideTitle && (
        <GraphTitle>
          <Typography variant="subtitle2">{t('wan.graphs.loss.title')}</Typography>
          {!isExpanded && (
            <GraphExpandButton onClick={onExpand}>
              <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
            </GraphExpandButton>
          )}
        </GraphTitle>
      )}
      <GraphWrapper>
        <ResponsiveContainer width="100%" height={isExpanded ? 400 : 200}>
          <AreaChart
            data={filteredDisplayData}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="simplifiedTime"
              tick={{ ...axisStyle }}
              ticks={xAxisTicks}
            />
            <YAxis
              tickFormatter={formatYAxisLoss}
              tick={{ ...axisStyle }}
            />
            <RechartsTooltip content={<LossTooltip />} />
            <Area
              type="monotone"
              dataKey="loss"
              name={t('wan.graphs.loss.loss')}
              fill="#F5F8FC"
              stroke={LOSS_COLOR}
              strokeWidth={isExpanded ? 2 : 1}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </GraphWrapper>
    </GraphContainer>
  )
}

export default LossGraph
